import { useMemo } from 'react'

const CORNER_ORDER = ['V1', 'V3', 'V4', 'V2']
const DIMENSION_ORDER = ['V1', 'V2', 'V3', 'V4']

const DIRS = {
  V1: { x: -1, y: -1 },
  V2: { x: -1, y: 1 },
  V3: { x: 1, y: -1 },
  V4: { x: 1, y: 1 },
}

const MAX_RADIUS = 90
const MAX_CORNER = MAX_RADIUS * Math.SQRT1_2

const LABEL_TWEAKS = {
  V1: { x: 11.6, y: 11.6 },
  V2: { x: 5.6, y: 4.4 },
  V3: { x: -8.6, y: 13.6 },
  V4: { x: -8.6, y: 21.4 },
}

const LABEL_POSITIONS = Object.fromEntries(
  Object.entries(DIRS).map(([id, dir]) => [
    id,
    {
      x: Math.round(dir.x * MAX_CORNER + LABEL_TWEAKS[id].x),
      y: Math.round(dir.y * MAX_CORNER + LABEL_TWEAKS[id].y),
    },
  ]),
)

const DEFAULT_AXIS_LABELS = {
  top: '目的地相关',
  right: '增益',
  bottom: '个人相关',
  left: '痛点',
}

const GRID_RADII = [90, 67.5, 45, 22.5]

const COLORS = {
  grid: '#e0dfe4',
  axis: '#3a3a3a',
  axisLabel: '#3a3a3a',
  quadrantLabelMuted: '#6b7280',
  quadrantLabelActive: '#3a3a3a',
  dominantLabel: '#c41230',
  polygonFill: 'rgba(196,18,48,0.1)',
  solidEdge: '#c41230',
  dashedEdge: '#b88a90',
  confirmedPoint: '#c41230',
  speculativePoint: '#8a8a8a',
  v5Ring: '#c41230',
  v5Fill: '#fff',
  v5Diamond: '#c41230',
  v5Text: '#c41230',
}

const isSolidStatus = (status) => status === 'confirmed'
const isDrawable = (status) => status !== 'unknown'

function computePoint(id, intensity) {
  const dir = DIRS[id]
  const radius = (intensity / 100) * MAX_RADIUS
  return {
    x: radius * dir.x * Math.SQRT1_2,
    y: radius * dir.y * Math.SQRT1_2,
  }
}

function resolveConfig(items, dominantIdProp) {
  const list = Array.isArray(items) ? items.filter((item) => item && item.key && typeof item.intensity === 'number') : []
  const byKey = new Map(list.map((item) => [item.key, item]))
  const present = DIMENSION_ORDER.filter((id) => byKey.has(id))

  const dimensions = {}
  for (const id of present) {
    const item = byKey.get(id)
    dimensions[id] = {
      name: item.name || '',
      summary: item.summary || '',
      intensity: item.intensity,
      status: item.status || 'unknown',
    }
  }

  const dominant = list.find((item) => item.dominant)?.key
  const dominantId =
    dominant ||
    (dominantIdProp && present.includes(dominantIdProp) ? dominantIdProp : null) ||
    (present.length ? present.reduce((best, id) => (dimensions[id].intensity > dimensions[best].intensity ? id : best), present[0]) : null)

  return { dimensions, dominantId, present }
}

export function ModelRadarChart({ items, dominantId, className }) {
  const config = useMemo(() => resolveConfig(items, dominantId), [items, dominantId])

  const { dimensions, present } = config

  const corners = CORNER_ORDER.filter((id) => present.includes(id) && isDrawable(dimensions[id].status))
  const points = {}
  for (const id of present) {
    points[id] = computePoint(id, dimensions[id].intensity)
  }

  const drawShape = corners.length >= 3
  const edges = []
  let polygonPoints = ''
  if (drawShape) {
    for (let i = 0; i < corners.length; i += 1) {
      edges.push([corners[i], corners[(i + 1) % corners.length]])
    }
    polygonPoints = corners.map((id) => `${points[id].x},${points[id].y}`).join(' ')
  }

  return (
    <svg viewBox='0 0 300 280' width='100%' height='100%' xmlns='http://www.w3.org/2000/svg' className={className}>
      <g transform='translate(150,140)'>
        {GRID_RADII.map((r) => (
          <circle key={r} cx='0' cy='0' r={r} fill='none' stroke={COLORS.grid} strokeWidth='0.6' />
        ))}

        <line x1={-MAX_CORNER} y1={-MAX_CORNER} x2={MAX_CORNER} y2={MAX_CORNER} stroke={COLORS.grid} strokeWidth='0.5' opacity='0.6' />
        <line x1={-MAX_CORNER} y1={MAX_CORNER} x2={MAX_CORNER} y2={-MAX_CORNER} stroke={COLORS.grid} strokeWidth='0.5' opacity='0.6' />

        <line x1='0' y1='-100' x2='0' y2='100' stroke={COLORS.axis} strokeWidth='1.5' />
        <polygon points='0,-108 -5,-96 5,-96' fill={COLORS.axis} />
        <polygon points='0,108 -5,96 5,96' fill={COLORS.axis} />
        <line x1='-110' y1='0' x2='110' y2='0' stroke={COLORS.axis} strokeWidth='1.5' />
        <polygon points='118,0 106,-5 106,5' fill={COLORS.axis} />
        <polygon points='-118,0 -106,-5 -106,5' fill={COLORS.axis} />

        <text x='0' y='-114' textAnchor='middle' fontSize='13' fill={COLORS.axisLabel} fontWeight='600'>
          {DEFAULT_AXIS_LABELS.top}
        </text>
        <text x='120' y='4' textAnchor='start' fontSize='13' fill={COLORS.axisLabel} fontWeight='600'>
          {DEFAULT_AXIS_LABELS.right}
        </text>
        <text x='0' y='120' textAnchor='middle' fontSize='13' fill={COLORS.axisLabel} fontWeight='600'>
          {DEFAULT_AXIS_LABELS.bottom}
        </text>
        <text x='-120' y='4' textAnchor='end' fontSize='13' fill={COLORS.axisLabel} fontWeight='600'>
          {DEFAULT_AXIS_LABELS.left}
        </text>

        {present.map((id) => {
          const dim = dimensions[id]
          const pos = LABEL_POSITIONS[id]
          const isDominant = config.dominantId === id
          const isActive = dim.status === 'confirmed' || dim.status === 'mixed'
          const fill = isDominant ? COLORS.dominantLabel : isActive ? COLORS.quadrantLabelActive : COLORS.quadrantLabelMuted
          const weight = isDominant ? '700' : isActive ? '600' : '500'
          const fontSize = isDominant ? '13' : '12'
          return (
            <text key={`label-${id}`} x={pos.x} y={pos.y} textAnchor='middle' fontSize={fontSize} fill={fill} fontWeight={weight}>
              {[id, dim.name].filter(Boolean).join(' ')}
            </text>
          )
        })}

        {drawShape && <polygon points={polygonPoints} fill={COLORS.polygonFill} />}

        {edges.map(([a, b]) => {
          const sourceA = dimensions[a].status
          const sourceB = dimensions[b].status
          const solid = isSolidStatus(sourceA) && isSolidStatus(sourceB)
          const pa = points[a]
          const pb = points[b]
          return (
            <line
              key={`edge-${a}-${b}`}
              x1={pa.x}
              y1={pa.y}
              x2={pb.x}
              y2={pb.y}
              stroke={solid ? COLORS.solidEdge : COLORS.dashedEdge}
              strokeWidth={solid ? '2' : '1.2'}
              strokeDasharray={solid ? undefined : '4,3'}
            />
          )
        })}

        {present.map((id) => {
          const dim = dimensions[id]
          const pos = points[id]
          const isDominant = config.dominantId === id
          const status = dim.status

          if (isDominant) {
            return (
              <g key={`point-${id}`}>
                <circle cx={pos.x} cy={pos.y} r='14' fill={COLORS.confirmedPoint} stroke='#fff' strokeWidth='2' />
                <text x={pos.x} y={pos.y + 6} textAnchor='middle' fontSize='16' fill='#fff' fontWeight='700'>
                  ★
                </text>
                <title>{dim.summary}</title>
              </g>
            )
          }

          if (status === 'unknown') {
            return null
          }

          if (status === 'confirmed') {
            return (
              <circle key={`point-${id}`} cx={pos.x} cy={pos.y} r='7' fill={COLORS.confirmedPoint}>
                <title>{dim.summary}</title>
              </circle>
            )
          }

          if (status === 'mixed') {
            return (
              <g key={`point-${id}`}>
                <circle cx={pos.x} cy={pos.y} r='7' fill={COLORS.confirmedPoint} />
                <circle cx={pos.x} cy={pos.y} r='10.5' fill='none' stroke={COLORS.speculativePoint} strokeWidth='1.2' strokeDasharray='2,1.5' />
                <title>{dim.summary}</title>
              </g>
            )
          }

          return (
            <g key={`point-${id}`}>
              <circle cx={pos.x} cy={pos.y} r='7' fill='none' stroke={COLORS.speculativePoint} strokeWidth='1.5' strokeDasharray='2,1.5' />
              {status === 'needs_verification' && (
                <text x={pos.x} y={pos.y - 13} textAnchor='middle' fontSize='9' fill={COLORS.speculativePoint}>
                  待核验
                </text>
              )}
              <title>{dim.summary}</title>
            </g>
          )
        })}

        <circle cx='0' cy='0' r='16' fill={COLORS.v5Fill} stroke={COLORS.v5Ring} strokeWidth='2' />
        <polygon points='-6,0 0,-6 6,0 0,6' fill={COLORS.v5Diamond} />
        <text x='6' y='3' textAnchor='start' fontSize='11' fill={COLORS.v5Text} fontWeight='700'>
          V5
        </text>
      </g>
    </svg>
  )
}
